import { useState } from "react";
import { Link } from "react-router";
import { BillingRateGroups } from "@/components/billing/BillingRateList";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { functionTabShellStyle } from "@/components/common/tab-shell";
import type { BillingRateGroup } from "@/lib/billing";
import { ACTION_LINK_CLASS, BODY_LINK_CLASS } from "@/lib/link-styles";

type Children = { children: React.ReactNode };
/** DocSectionLink の飛び先にする見出しに付ける ID。 */
type HeadingProps = Children & { id?: string };

// 利用ガイドの各ページで見た目を揃えるための部品

export function DocPage({ title, children }: { title: string; children?: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-4 text-ui text-fg-1">
			<h1 className="m-0 text-display font-semibold tracking-snug">{title}</h1>
			{children}
		</div>
	);
}

export function DocHeading({ id, children }: HeadingProps) {
	return (
		<h2 id={id} className="m-0 mt-4 text-heading font-semibold tracking-snug">
			{children}
		</h2>
	);
}

export function DocSubheading({ id, children }: HeadingProps) {
	return (
		<h3 id={id} className="m-0 mt-2 -mb-2 text-ui font-semibold tracking-snug">
			{children}
		</h3>
	);
}

export function DocParagraph({ children }: Children) {
	return <p className="m-0">{children}</p>;
}

export function DocList({ ordered = false, children }: Children & { ordered?: boolean }) {
	const className = "m-0 flex flex-col gap-1 pl-6";
	return ordered ? (
		<ol className={`${className} list-decimal`}>{children}</ol>
	) : (
		<ul className={`${className} list-disc`}>{children}</ul>
	);
}

/** 見出しの下に置く、その章の読み手。 */
export function DocAudience({ children }: Children) {
	return <p className="m-0 -mt-2 text-caption text-fg-2">{children}</p>;
}

/** 本文中のコマンド・パス・キー。 */
export function DocCode({ children }: Children) {
	return <code className="rounded-1 bg-surface-2 px-1 font-mono text-caption">{children}</code>;
}

/** 端末で実行するコマンド。1 行ずつ枠で囲み、コピーボタンを付ける。 */
export function DocCommand({ command }: { command: string }) {
	return (
		<div className="my-1 flex items-center gap-2 rounded-2 border border-line-1 bg-surface-2 py-2 pr-2 pl-3">
			<code className="min-w-0 flex-1 font-mono text-caption break-all">{command}</code>
			<CopyIconButton value={command} label="コマンドをコピー" />
		</div>
	);
}

/** 箇条書きの先頭に置く項目名。 */
export function DocTerm({ children }: Children) {
	return <strong className="font-semibold">{children}</strong>;
}

/** エージェントへの依頼の例文。1 つずつ枠で囲み、コピーボタンを付ける。 */
export function DocExamplePrompts({ prompts }: { prompts: string[] }) {
	return (
		<ul className="m-0 flex list-none flex-col gap-2 p-0">
			{prompts.map((prompt) => (
				<li
					key={prompt}
					className="flex items-start gap-2 rounded-2 border border-line-1 bg-surface-2 py-2 pr-2 pl-3"
				>
					<span className="flex-1">{prompt}</span>
					<CopyIconButton value={prompt} label="例文をコピー" />
				</li>
			))}
		</ul>
	);
}

/** 単価表。課金ダッシュボードの単価ダイアログと同じ行を枠で囲んで見せる。 */
export function DocRateTable({ groups }: { groups: readonly BillingRateGroup[] }) {
	return (
		<div className="rounded-2 border border-line-1 bg-surface-2 px-3 py-2">
			<BillingRateGroups groups={groups} />
		</div>
	);
}

/** 表。行が多くてもページが縦に伸びすぎないよう、一定の高さを超えたら表の中だけをスクロールさせる。nowrap の列は折り返さない。 */
export function DocScrollTable({
	columns,
	rows,
}: {
	columns: readonly { label: string; align?: "left" | "right"; nowrap?: boolean }[];
	rows: readonly { key: string; cells: readonly React.ReactNode[] }[];
}) {
	return (
		<div className="max-h-80 overflow-y-auto rounded-2 border border-line-1 bg-surface-2">
			<table className="w-full border-collapse text-caption">
				<thead className="sticky top-0 bg-surface-2">
					<tr>
						{columns.map((column) => (
							<th
								key={column.label}
								className={`border-b border-line-1 px-3 py-2 font-semibold text-fg-2 ${column.align === "right" ? "text-right" : "text-left"}`}
							>
								{column.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr key={row.key}>
							{row.cells.map((cell, i) => (
								<td
									key={columns[i]?.label ?? i}
									className={`px-3 py-1.5 align-top ${columns[i]?.align === "right" ? "text-right tabular-nums whitespace-nowrap" : "text-left"} ${columns[i]?.nowrap ? "whitespace-nowrap" : ""}`}
								>
									{cell}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/**
 * タブで切り替えて見せる内容。項目が多く縦に並べると読みにくいもの (モデル別の単価など) に使う。
 * framed は、切り替わる範囲が本文と見分けにくいとき (手順の説明など) に、タブと中身を枠で囲む。
 */
export function DocTabs({
	tabs,
	framed = false,
}: {
	tabs: readonly { key: string; label: string; content: React.ReactNode }[];
	framed?: boolean;
}) {
	const [activeKey, setActiveKey] = useState<string | null>(null);
	const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];
	if (active === undefined) return null;
	return (
		<div
			className={
				framed
					? "flex flex-col gap-3 rounded-2 border border-line-1 px-4 pb-4"
					: "flex flex-col gap-3"
			}
		>
			<div className="flex flex-wrap gap-x-4 border-b border-line-1">
				{tabs.map((tab) => (
					<button
						key={tab.key}
						type="button"
						data-testid={`doc-tab-${tab.key}`}
						onClick={() => setActiveKey(tab.key)}
						style={{ ...functionTabShellStyle(tab.key === active.key), height: 32 }}
					>
						{tab.label}
					</button>
				))}
			</div>
			<div className="flex flex-col gap-4">{active.content}</div>
		</div>
	);
}

/**
 * 見出し (id 付きの DocHeading・DocSubheading) へのリンク。page を渡すとそのページの見出しへ同じタブで移る。
 * URL のハッシュを更新し、スクロールは DocsPage が行う。
 */
export function DocSectionLink({ id, page, children }: Children & { id: string; page?: string }) {
	return (
		<Link to={page ? `/docs/${page}#${id}` : `#${id}`} className={BODY_LINK_CLASS}>
			{children}
		</Link>
	);
}

/** ページ末尾の「次：…」リンク。同じタブで次のページへ移る。 */
export function DocNextLink({ to, children }: { to: string; children: React.ReactNode }) {
	return (
		<p className="m-0 mt-4">
			<Link to={to} className={ACTION_LINK_CLASS}>
				次：{children} →
			</Link>
		</p>
	);
}
